// frontend/src/components/MapView.tsx
import { apiUrl, API_BASE_URL } from '../config/paths';
// Версия: 2.1.0 (2025-12-10) - локализация статусов, поддержка URL параметров
//
// ИЗМЕНЕНИЯ:
// - Добавлен useSearchParams для чтения lat, lon, status, id из URL
// - Центрирование карты на координаты из URL
// - Локализация статусов через getSignalStatusLabel
// - Поддержка всех форматов координат: lat/lon, latitude/longitude, lng
// ИСПРАВЛЕНО 2026-01-13: apiUrl() с двумя аргументами → один аргумент

import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, Alert, Chip, CircularProgress } from '@mui/material';
import MarineMap from './MarineMap';
import { getSignalStatusLabel } from '../utils/statusLabels';

// Хелпер для проверки наличия координат в любом формате
const hasCoordinates = (signal: any): boolean => {
  // Формат 1: coordinates: { lat, lon }
  if (signal.coordinates?.lat !== undefined && signal.coordinates?.lon !== undefined) {
    return true;
  }
  // Формат 2: coordinates: { latitude, longitude }
  if (signal.coordinates?.latitude !== undefined && signal.coordinates?.longitude !== undefined) {
    return true;
  }
  // Формат 3: coordinates: { lat, lng }
  if (signal.coordinates?.lat !== undefined && signal.coordinates?.lng !== undefined) {
    return true;
  }
  // Формат 4: плоские поля latitude, longitude
  if (signal.latitude !== undefined && signal.longitude !== undefined) {
    return true;
  }
  return false;
};

const MapView = () => {
  const [searchParams] = useSearchParams();
  const [signals, setSignals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSignals, setTotalSignals] = useState<number>(0);

  // Читаем параметры из URL (от двойного клика в SignalMonitor)
  const urlLat = searchParams.get('lat');
  const urlLon = searchParams.get('lon');
  const urlStatus = searchParams.get('status');
  const urlId = searchParams.get('id');

  // Определяем центр карты
  const mapCenter: [number, number] = useMemo(() => {
    if (urlLat && urlLon) {
      return [parseFloat(urlLon), parseFloat(urlLat)]; // OpenLayers: [lon, lat]
    }
    return [131.9, 43.1]; // Дефолт: Владивосток
  }, [urlLat, urlLon]);

  // Определяем зум (ближе если есть конкретная точка)
  const mapZoom = useMemo(() => {
    return urlLat && urlLon ? 10 : 5;
  }, [urlLat, urlLon]);

  useEffect(() => {
    fetchSignals();
  }, []);

  const fetchSignals = async () => {
    try {
      // ИСПРАВЛЕНО: apiUrl() принимает только путь, options идут в fetch()
      const response = await fetch(apiUrl('signals'), {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        // Поддержка форматов: массив, {data: []}, {items: []}
        const signalsArray = Array.isArray(data) ? data : data?.items || data?.data || [];
        setSignals(signalsArray);
        // Используем total из нового формата ответа, если доступен
        if (data?.total != null) {
          setTotalSignals(data.total);
        } else {
          // Fallback: отдельный запрос stats
          try {
            const statsRes = await fetch(apiUrl('signals/stats'), {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
            });
            if (statsRes.ok) {
              const statsData = await statsRes.json();
              setTotalSignals(statsData?.allTime?.total || statsData?.total || signalsArray.length);
            }
          } catch {
            /* fallback — оставить текущее */
          }
        }
      }
    } catch (error) {
      console.error('Error loading signals:', error);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  // Получаем сигналы с координатами + добавляем "выбранный" сигнал из URL если его нет в списке
  const signalsWithCoords = useMemo(() => {
    const filtered = signals.filter((s: any) => hasCoordinates(s));

    // Если есть координаты из URL, проверяем есть ли этот сигнал в списке
    if (urlLat && urlLon && urlId) {
      const exists = filtered.some((s: any) => s.id === parseInt(urlId));
      if (!exists) {
        // Добавляем виртуальный сигнал для отображения на карте
        filtered.push({
          id: parseInt(urlId),
          coordinates: {
            lat: parseFloat(urlLat),
            lon: parseFloat(urlLon),
          },
          status: urlStatus || 'UNKNOWN',
          signal_type: 'TEST', // Тип по умолчанию
          vessel_name: `Сигнал #${urlId}`,
          terminal_number: 'из таблицы',
        });
      }
    }

    return filtered;
  }, [signals, urlLat, urlLon, urlId, urlStatus]);

  const getStatistics = () => {
    const withCoords = signals.filter((s: any) => hasCoordinates(s)).length;
    const distress = signals.filter(
      (s: any) => s.signal_type === 'DISTRESS' || s.signal_type === 'REAL_ALERT'
    ).length;
    const test = signals.filter(
      (s: any) => s.signal_type === 'TEST' || s.signal_type === 'SSAS_TEST'
    ).length;
    return { total: signals.length, withCoords, distress, test };
  };

  const stats = getStatistics();

  // Информация о выбранном сигнале (локализованный статус)
  const selectedInfo =
    urlId && urlLat && urlLon
      ? {
          id: urlId,
          lat: parseFloat(urlLat).toFixed(4),
          lon: parseFloat(urlLon).toFixed(4),
          status: urlStatus ? getSignalStatusLabel(urlStatus) : null,
        }
      : null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Карта сигналов ССТО
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Chip label={`Всего сигналов: ${totalSignals || stats.total}`} />
        <Chip label={`С координатами: ${stats.withCoords}`} color="primary" />
        {stats.distress > 0 && <Chip label={`Тревожных: ${stats.distress}`} color="error" />}
        {stats.test > 0 && <Chip label={`Тестовых: ${stats.test}`} color="info" />}
      </Box>

      {/* Информация о выбранном сигнале — локализованный статус */}
      {selectedInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Выбран сигнал #{selectedInfo.id}</strong> — координаты: {selectedInfo.lat}°,{' '}
          {selectedInfo.lon}°{selectedInfo.status && ` — статус: ${selectedInfo.status}`}
        </Alert>
      )}

      {loading ? (
        <Paper
          sx={{
            p: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Загрузка сигналов...</Typography>
        </Paper>
      ) : (
        <>
          {stats.withCoords === 0 && !selectedInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Нет сигналов с координатами для отображения на карте. Попробуйте создать тестовый
              сигнал через раздел "Тестирование".
            </Alert>
          )}

          <Paper sx={{ p: 1 }}>
            <MarineMap
              signals={signalsWithCoords}
              center={mapCenter}
              zoom={mapZoom}
              skipAutoFit={!!(urlLat && urlLon)}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MapView;
